import * as React from "react";
import { BuilderContext, useDrawer } from "react-flow-builder";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { Text, Grid, Button, Modal, ActionIcon, Title, Divider, useMantineTheme } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import {
  CTextInput,
  CSelect,
  CTextarea,
  CMultiSelect,
  CMultiSelectCreatable,
} from "@supportinitiative/react-hook-form-mantine";

import { ReconsetCVMContext } from "../../ReconsetCVM";
import Where from "../QueryMatch/Where";

import { yupResolver } from "@hookform/resolvers/yup";
import { useActiveRoles } from "../../../hooks";
import { getId } from "../../../utilities";
import { NotificationActionSchema, NotificationActionType } from "@supportinitiative/common";

function Notification() {
  const { selectedNode: node } = React.useContext(BuilderContext);
  const { closeDrawer: cancel, saveDrawer: save } = useDrawer();
  const { state: reconsetFormState } = React.useContext(ReconsetCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<NotificationActionType>({
    defaultValues: node?.data || {
      name: "",
      type: "EMAIL",
      to: [],
      subject: "",
      body: "",
      dataPopulation: { columns: [], rows: [] },
    },
    criteriaMode: "all",
    resolver: yupResolver(NotificationActionSchema),
  });

  const theme = useMantineTheme();

  const columnsDataPopulation = useFieldArray({
    control,
    name: "dataPopulation.columns",
  });

  const rowsDataPopulation = useFieldArray({
    control,
    name: "dataPopulation.rows",
  });

  const to = useWatch({
    control,
    name: "to",
  });

  const toLOV = to
    ? to.map((item) => {
        return { label: item, value: item };
      })
    : [];

  return (
    <Modal centered size={"xl"} opened={true} onClose={cancel} title={<Title order={4}>Summary Stats</Title>}>
      <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={12}>
          <CTextInput
            name={`name`}
            control={control}
            showErrorText={false}
            label="Name"
            required
            disabled={node?.disabled || false}
          />
        </Grid.Col>
      </Grid>
      <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={12}>
          <CSelect
            name={`type`}
            control={control}
            showErrorText={false}
            label="Type"
            data={[
              { label: "EMAIL", value: "EMAIL" },
              { label: "SMS", value: "SMS" },
            ]}
            required
            disabled={node?.disabled || false}
          />
        </Grid.Col>
      </Grid>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // borderTop: "2px solid #ccc",
          // paddingTop: "20px",
          // width: "90%",
        }}
      >
        <Divider my="xs" label="Data Population" labelPosition="center" />

        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Columns
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              columnsDataPopulation.append({
                column: "",
                genFunction: "",
                id: getId(),
              });
            }}
            disabled={reconsetFormState.actionOptions.disableAll}
          >
            <IconPlus />
          </ActionIcon>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {columnsDataPopulation.fields.map((field, index) => {
            return (
              <Grid gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={3}>
                  <CTextInput
                    name={`dataPopulation.columns.${index}.column`}
                    control={control}
                    label={"Column"}
                    disabled={reconsetFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <CTextarea
                    name={`dataPopulation.columns.${index}.genFunction`}
                    control={control}
                    label={"Generate Function"}
                    minRows={4}
                    disabled={reconsetFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <CTextInput
                    name={`dataPopulation.columns.${index}.id`}
                    control={control}
                    label={"Id"}
                    disabled={true}
                  />
                </Grid.Col>
                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="white"
                    color="gray"
                    onClick={() => {
                      columnsDataPopulation.remove(index);
                    }}
                    disabled={reconsetFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: theme.spacing.lg,
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Rows
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              rowsDataPopulation.append({
                column: "",
                genFunction: "",
                id: getId(),
              });
            }}
            disabled={reconsetFormState.actionOptions.disableAll}
          >
            <IconPlus />
          </ActionIcon>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {rowsDataPopulation.fields.map((field, index) => {
            return (
              <Grid gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={3}>
                  <CTextInput
                    name={`dataPopulation.rows.${index}.column`}
                    control={control}
                    label={"Column"}
                    disabled={reconsetFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <CTextarea
                    name={`dataPopulation.rows.${index}.genFunction`}
                    control={control}
                    label={"Generate Function"}
                    minRows={4}
                    disabled={reconsetFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <CTextInput name={`dataPopulation.rows.${index}.id`} control={control} label={"Id"} disabled={true} />
                </Grid.Col>
                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="white"
                    color="gray"
                    onClick={() => {
                      rowsDataPopulation.remove(index);
                    }}
                    disabled={reconsetFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // borderTop: "2px solid #ccc",
          // paddingTop: "20px",
          // width: "90%",
        }}
      >
        <Divider my="xs" label="Condition" labelPosition="center" />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextarea
                name={`condition`}
                control={control}
                label="Condition"
                autosize
                minRows={5}
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // borderTop: "2px solid #ccc",
          // paddingTop: "20px",
          // width: "90%",
        }}
      >
        <Divider my="xs" label="Details" labelPosition="center" />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
            <Grid.Col span={12}>
              <CMultiSelectCreatable
                name={"to"}
                placeholder="To"
                control={control}
                data={toLOV}
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextInput
                name={`subject`}
                control={control}
                label="Subject"
                required
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextarea
                name={`body`}
                control={control}
                label="Body"
                required
                autosize
                minRows={10}
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </div>
      </div>

      <Grid align="stretch" gutter={"xs"} mt="xs">
        <Grid.Col span={2}>
          <Button
            color="teal"
            variant="filled"
            fullWidth
            type="submit"
            onClick={
              node?.disabled
                ? () => {
                    cancel();
                  }
                : async (event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    const values = getValues();
                    // console.log("values", values);
                    clearErrors();
                    try {
                      await NotificationActionSchema.validate(values, {
                        abortEarly: false,
                      });

                      save?.(values);
                      //cancel();
                    } catch (error) {
                      console.log("errors", error);
                      console.log("error inner", JSON.stringify(error));
                      (error as any).inner.forEach((err: any) => {
                        setError(err.path, {
                          type: "custom",
                          message: err.message,
                        });
                      });
                    }
                  }
            }
          >
            Ok
          </Button>
        </Grid.Col>
      </Grid>
    </Modal>
  );
}

export default Notification;
